import { Popover as PopoverPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/utils';

export const PopoverRoot = PopoverPrimitive.Root;

export const PopoverTrigger = PopoverPrimitive.Trigger;

export const PopoverAnchor = PopoverPrimitive.Anchor;

export const PopoverArrow = PopoverPrimitive.Arrow;

export interface PopoverContentProps extends React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> {
  container?: HTMLElement | null;
  /** Render the popover inside a portal. Default: `true` */
  portalled?: boolean;
  animated?: boolean;
}

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ container, portalled = true, ...props }, ref) =>
  portalled ? (
    <PopoverPrimitive.Portal container={container}>
      <PopoverContentNoPortal ref={ref} {...props} />
    </PopoverPrimitive.Portal>
  ) : (
    <PopoverContentNoPortal ref={ref} {...props} />
  )
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

/**
 * Popover content that renders without a portal
 */
const PopoverContentNoPortal = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  Omit<PopoverContentProps, 'container' | 'portalled'>
>(
  (
    { className, align = 'center', sideOffset = 4, animated = true, ...props },
    ref
  ) => (
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'z-50 w-72 rounded-xl border-opacity-15 p-2.5',
        'text-foreground shadow-sm dark:shadow-md outline-none',
        'bg-surface border border-border-strong',
        'origin-[--radix-popover-content-transform-origin]',
        animated && [
          'motion-ease-out',
          'data-[state=open]:motion-duration-200',
          'data-[state=open]:motion-scale-in-95',
          'data-[state=open]:motion-opacity-in-0',
          'data-[state=closed]:motion-duration-150',
          'data-[state=closed]:motion-scale-out-95',
          'data-[state=closed]:motion-opacity-out-0',
        ],
        className
      )}
      {...props}
    />
  )
);
